import {
  collection,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  DocumentSnapshot,
  Timestamp,
  serverTimestamp,
  writeBatch,
  QueryConstraint,
  increment,
  DocumentData,
} from 'firebase/firestore';
import { deleteUser as deleteAuthUser } from 'firebase/auth';
import { db } from '../config/firebase';
import { UserRole } from '../types/auth';
import { UserEntity, UserListItem, UserStats } from '../types/user';
import {
  UserDetailAdmin,
  BulkActionRequest,
  BulkActionResult,
  UserSuspension,
  AdminStats,
  UserActivitySummary,
} from '../types/admin';
import { auditLogService } from './auditLogService';
import { ChangeTrackingService } from './changeTrackingService';
import { NotificationService } from './notificationService';
import { NotificationPriority } from '../types/user';

/**
 * Helper function to safely convert Firestore timestamp or Date to Date
 * Handles cases where the value is already a Date object or a Firestore Timestamp
 */
const toDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  if (typeof value.toDate === 'function') return value.toDate();
  return undefined;
};

/**
 * Admin Service
 * Handles all administrative operations for user management, content moderation, and system administration
 *
 * This service provides methods for:
 * - User management (list, approve, reject, delete, suspend)
 * - Role management (change roles, validate role hierarchy)
 * - Bulk operations (approve multiple users, delete multiple users)
 * - User statistics and analytics
 * - User activity tracking
 *
 * All operations are logged to the audit log for accountability.
 */
export class AdminService {
  // ============================================================================
  // User Management Methods
  // ============================================================================

  /**
   * Get all users with optional filtering and pagination
   * @param filters - Optional filters for role, approval status, etc.
   * @param pageSize - Number of users per page (default: 25)
   * @param lastDoc - Last document from previous page (for pagination)
   * @returns Array of users and pagination info
   */
  static async getAllUsers(
    filters?: {
      role?: UserRole;
      isApproved?: boolean;
      isEmailVerified?: boolean;
      search?: string;
    },
    pageSize: number = 25,
    lastDoc?: DocumentSnapshot
  ): Promise<{ users: UserListItem[]; lastDoc: DocumentSnapshot | null; hasMore: boolean }> {
    try {
      const constraints: QueryConstraint[] = [];

      // Add filters
      if (filters?.role) {
        constraints.push(where('role', '==', filters.role));
      }
      if (filters?.isApproved !== undefined) {
        constraints.push(where('isApproved', '==', filters.isApproved));
      }
      if (filters?.isEmailVerified !== undefined) {
        constraints.push(where('isEmailVerified', '==', filters.isEmailVerified));
      }

      // Add ordering and pagination
      constraints.push(orderBy('createdAt', 'desc'));
      constraints.push(limit(pageSize + 1)); // Fetch one extra to check if there are more

      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      const q = query(collection(db, 'users'), ...constraints);
      const snapshot = await getDocs(q);

      const users: UserListItem[] = [];
      let newLastDoc: DocumentSnapshot | null = null;
      let hasMore = false;

      snapshot.docs.forEach((doc, index) => {
        if (index < pageSize) {
          const data = doc.data();
          users.push({
            uid: doc.id,
            email: data.email,
            displayName: data.displayName,
            role: data.role,
            isApproved: data.isApproved,
            profileComplete: data.profileComplete,
            isEmailVerified: data.isEmailVerified,
            lastLoginAt: toDate(data.lastLoginAt),
            createdAt: toDate(data.createdAt) || new Date(),
            personId: data.personId,
          });
          newLastDoc = doc;
        } else {
          hasMore = true;
        }
      });

      // Apply search filter in memory if provided (Firestore doesn't support full-text search)
      if (filters?.search) {
        const searchLower = filters.search.toLowerCase();
        return {
          users: users.filter(
            (user) =>
              user.displayName.toLowerCase().includes(searchLower) ||
              user.email.toLowerCase().includes(searchLower)
          ),
          lastDoc: newLastDoc,
          hasMore,
        };
      }

      return { users, lastDoc: newLastDoc, hasMore };
    } catch (error) {
      console.error('Error getting all users:', error);
      throw new Error('Failed to retrieve users');
    }
  }

  /**
   * Get detailed information about a specific user
   * @param userId - User ID to retrieve
   * @returns Detailed user information
   */
  static async getUserById(userId: string): Promise<UserDetailAdmin> {
    try {
      const userDoc = await getDoc(doc(db, 'users', userId));

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const data = userDoc.data();

      // Get user stats (simplified - in production, these would be aggregated)
      const stats = await this.getUserStats(userId);

      const userDetail: UserDetailAdmin = {
        uid: userDoc.id,
        email: data.email,
        displayName: data.displayName,
        firstName: data.firstName,
        lastName: data.lastName,
        photoURL: data.photoURL,
        phoneNumber: data.phoneNumber,
        bio: data.bio,
        role: data.role,
        isApproved: data.isApproved,
        isSuspended: data.isSuspended || false,
        suspendedAt: toDate(data.suspendedAt),
        suspendedBy: data.suspendedBy,
        suspendedReason: data.suspendedReason,
        isEmailVerified: data.isEmailVerified,
        profileComplete: data.profileComplete,
        createdAt: toDate(data.createdAt) || new Date(),
        lastLoginAt: toDate(data.lastLoginAt),
        lastActivityAt: toDate(data.lastActivityAt),
        approvedAt: toDate(data.approvedAt),
        loginCount: stats.loginCount,
        personsCreated: stats.personsCreated,
        photosUploaded: stats.photosUploaded,
        commentsCreated: stats.commentsCreated,
        relationshipsCreated: stats.relationshipsCreated,
        totalContributions: stats.personsCreated + stats.photosUploaded + stats.commentsCreated + stats.relationshipsCreated,
        personId: data.personId,
        registrationCode: data.registrationCode,
      };

      return userDetail;
    } catch (error) {
      console.error('Error getting user by ID:', error);
      throw new Error('Failed to retrieve user details');
    }
  }

  /**
   * Get users pending approval
   * @returns Array of users awaiting admin approval
   */
  static async getPendingApprovals(): Promise<UserListItem[]> {
    try {
      const q = query(
        collection(db, 'users'),
        where('isApproved', '==', false),
        orderBy('createdAt', 'asc')
      );

      const snapshot = await getDocs(q);

      const users: UserListItem[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        users.push({
          uid: doc.id,
          email: data.email,
          displayName: data.displayName,
          role: data.role,
          isApproved: data.isApproved,
          profileComplete: data.profileComplete,
          isEmailVerified: data.isEmailVerified,
          lastLoginAt: data.lastLoginAt?.toDate(),
          createdAt: data.createdAt?.toDate(),
          personId: data.personId,
        });
      });

      return users;
    } catch (error) {
      console.error('Error getting pending approvals:', error);
      throw new Error('Failed to retrieve pending approvals');
    }
  }

  /**
   * Approve a user's account
   * @param userId - User ID to approve
   * @param approvedBy - Admin user ID performing the approval
   */
  static async approveUser(userId: string, approvedBy: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();

      await updateDoc(userRef, {
        isApproved: true,
        approvedAt: serverTimestamp(),
        approvedBy,
        updatedAt: serverTimestamp(),
      });

      // Log the approval action
      await auditLogService.logAction({
        userId: approvedBy,
        action: 'user_approved',
        targetUserId: userId,
        details: {
          userEmail: userData.email,
          userName: userData.displayName,
        },
      });

      // Send approval notification (non-blocking)
      NotificationService.createNotification({
        userId,
        type: 'approval_granted',
        priority: NotificationPriority.HIGH,
        actionUrl: '/dashboard',
      }).catch(err => console.error('Failed to create approval notification:', err));

      console.log(`User ${userId} approved by ${approvedBy}`);
    } catch (error) {
      console.error('Error approving user:', error);
      throw new Error('Failed to approve user');
    }
  }

  /**
   * Reject a user's account and delete their account
   * @param userId - User ID to reject
   * @param rejectedBy - Admin user ID performing the rejection
   * @param reason - Reason for rejection
   */
  static async rejectUser(userId: string, rejectedBy: string, reason?: string): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();

      // Send rejection notification before deleting (non-blocking)
      NotificationService.createNotification({
        userId,
        type: 'approval_denied',
        priority: NotificationPriority.HIGH,
        data: { reason: reason || 'Your registration did not meet our requirements' },
      }).catch(err => console.error('Failed to create rejection notification:', err));

      // Log the rejection before deleting
      await auditLogService.logAction({
        userId: rejectedBy,
        action: 'user_rejected',
        targetUserId: userId,
        details: {
          userEmail: userData.email,
          userName: userData.displayName,
          reason: reason || 'No reason provided',
        },
      });

      // Delete the user document
      await deleteDoc(userRef);

      console.log(`User ${userId} rejected and deleted by ${rejectedBy}`);
    } catch (error) {
      console.error('Error rejecting user:', error);
      throw new Error('Failed to reject user');
    }
  }

  /**
   * Delete a user account permanently
   * @param userId - User ID to delete
   * @param deletedBy - Admin user ID performing the deletion
   * @param deletedByName - Admin user name performing the deletion
   * @param reason - Reason for deletion
   */
  static async deleteUser(
    userId: string,
    deletedBy: string,
    deletedByName: string,
    reason?: string
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const beforeState = { ...userData, id: userId };

      // Prevent deleting yourself
      if (userId === deletedBy) {
        throw new Error('You cannot delete your own account');
      }

      // Prevent deleting the last admin
      if (userData.role === 'admin') {
        const adminCount = await this.getAdminCount();
        if (adminCount <= 1) {
          throw new Error('Cannot delete the last admin user');
        }
      }

      // Delete the user document
      await deleteDoc(userRef);

      // Log the deletion (audit log)
      await auditLogService.logAction({
        userId: deletedBy,
        action: 'user_deleted',
        targetUserId: userId,
        details: {
          userEmail: userData.email,
          userName: userData.displayName,
          userRole: userData.role,
          reason: reason || 'No reason provided',
        },
      });

      // Log change tracking
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'user',
            entityId: userId,
            changeType: 'delete',
            beforeState: beforeState,
            afterState: null,
            changedFields: Object.keys(beforeState),
            reason: `User deleted: ${reason || 'No reason provided'}`,
          },
          deletedBy,
          deletedByName,
          true // Admin action
        );
      } catch (changeError) {
        console.error('Error logging user deletion change:', changeError);
      }

      console.log(`User ${userId} deleted by ${deletedBy}`);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  /**
   * Suspend a user account
   * @param userId - User ID to suspend
   * @param suspendedBy - Admin user ID performing the suspension
   * @param suspendedByName - Admin user name performing the suspension
   * @param reason - Reason for suspension
   */
  static async suspendUser(
    userId: string,
    suspendedBy: string,
    suspendedByName: string,
    reason: string
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const beforeState = { ...userData, id: userId };

      // Prevent suspending yourself
      if (userId === suspendedBy) {
        throw new Error('You cannot suspend your own account');
      }

      await updateDoc(userRef, {
        isSuspended: true,
        suspendedAt: serverTimestamp(),
        suspendedBy,
        suspendedReason: reason,
        updatedAt: serverTimestamp(),
      });

      // Get updated state
      const updatedDoc = await getDoc(userRef);
      const afterState = { ...updatedDoc.data(), id: userId };

      // Create suspension record
      const suspensionData: Omit<UserSuspension, 'id'> = {
        userId,
        suspendedBy,
        suspendedAt: new Date(),
        reason,
      };

      await this.createSuspensionRecord(suspensionData);

      // Log the suspension (audit log)
      await auditLogService.logAction({
        userId: suspendedBy,
        action: 'user_suspended',
        targetUserId: userId,
        details: {
          userEmail: userData.email,
          userName: userData.displayName,
          reason,
        },
      });

      // Log change tracking
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'user',
            entityId: userId,
            changeType: 'update',
            beforeState: beforeState,
            afterState: afterState,
            changedFields: ['isSuspended', 'suspendedAt', 'suspendedBy', 'suspendedReason'],
            reason: `User suspended: ${reason}`,
          },
          suspendedBy,
          suspendedByName,
          true // Admin action
        );
      } catch (changeError) {
        console.error('Error logging user suspension change:', changeError);
      }

      console.log(`User ${userId} suspended by ${suspendedBy}`);
    } catch (error) {
      console.error('Error suspending user:', error);
      throw error;
    }
  }

  /**
   * Unsuspend a user account
   * @param userId - User ID to unsuspend
   * @param unsuspendedBy - Admin user ID performing the unsuspension
   * @param unsuspendedByName - Admin user name performing the unsuspension
   */
  static async unsuspendUser(
    userId: string,
    unsuspendedBy: string,
    unsuspendedByName: string
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const beforeState = { ...userData, id: userId };

      await updateDoc(userRef, {
        isSuspended: false,
        suspendedAt: null,
        suspendedBy: null,
        suspendedReason: null,
        updatedAt: serverTimestamp(),
      });

      // Get updated state
      const updatedDoc = await getDoc(userRef);
      const afterState = { ...updatedDoc.data(), id: userId };

      // Update suspension record
      await this.updateSuspensionRecord(userId, unsuspendedBy);

      // Log the unsuspension (audit log)
      await auditLogService.logAction({
        userId: unsuspendedBy,
        action: 'user_unsuspended',
        targetUserId: userId,
        details: {
          userEmail: userData.email,
          userName: userData.displayName,
        },
      });

      // Log change tracking
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'user',
            entityId: userId,
            changeType: 'update',
            beforeState: beforeState,
            afterState: afterState,
            changedFields: ['isSuspended', 'suspendedAt', 'suspendedBy', 'suspendedReason'],
            reason: 'User unsuspended',
          },
          unsuspendedBy,
          unsuspendedByName,
          true // Admin action
        );
      } catch (changeError) {
        console.error('Error logging user unsuspension change:', changeError);
      }

      console.log(`User ${userId} unsuspended by ${unsuspendedBy}`);
    } catch (error) {
      console.error('Error unsuspending user:', error);
      throw new Error('Failed to unsuspend user');
    }
  }

  // ============================================================================
  // Role Management Methods
  // ============================================================================

  /**
   * Update a user's role
   * @param userId - User ID to update
   * @param newRole - New role to assign
   * @param changedBy - Admin user ID performing the change
   * @param changedByName - Admin user name performing the change
   */
  static async updateUserRole(
    userId: string,
    newRole: UserRole,
    changedBy: string,
    changedByName: string
  ): Promise<void> {
    try {
      const userRef = doc(db, 'users', userId);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data();
      const oldRole = userData.role;
      const beforeState = { ...userData, id: userId };

      // Prevent changing your own role
      if (userId === changedBy) {
        throw new Error('You cannot change your own role');
      }

      // Prevent demoting the last admin
      if (oldRole === 'admin' && newRole !== 'admin') {
        const adminCount = await this.getAdminCount();
        if (adminCount <= 1) {
          throw new Error('Cannot demote the last admin user');
        }
      }

      await updateDoc(userRef, {
        role: newRole,
        updatedAt: serverTimestamp(),
      });

      // Get updated state
      const updatedDoc = await getDoc(userRef);
      const afterState = { ...updatedDoc.data(), id: userId };

      // Log the role change (audit log)
      await auditLogService.logAction({
        userId: changedBy,
        action: 'role_changed',
        targetUserId: userId,
        details: {
          userEmail: userData.email,
          userName: userData.displayName,
          oldRole,
          newRole,
        },
      });

      // Send role change notification (non-blocking)
      NotificationService.createNotification({
        userId,
        type: 'role_changed',
        priority: NotificationPriority.HIGH,
        data: {
          oldRole,
          newRole,
          changedBy: changedByName,
        },
      }).catch(err => console.error('Failed to create role change notification:', err));

      // Log change tracking
      try {
        await ChangeTrackingService.logChange(
          {
            entityType: 'user',
            entityId: userId,
            changeType: 'update',
            beforeState: beforeState,
            afterState: afterState,
            changedFields: ['role'],
            reason: `User role changed from ${oldRole} to ${newRole}`,
          },
          changedBy,
          changedByName,
          true // Admin action
        );
      } catch (changeError) {
        console.error('Error logging user role change:', changeError);
      }

      console.log(`User ${userId} role changed from ${oldRole} to ${newRole} by ${changedBy}`);
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  /**
   * Get count of admin users
   * @returns Number of admin users
   */
  private static async getAdminCount(): Promise<number> {
    try {
      const q = query(collection(db, 'users'), where('role', '==', 'admin'));
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error('Error getting admin count:', error);
      return 0;
    }
  }

  // ============================================================================
  // Bulk Operations
  // ============================================================================

  /**
   * Perform bulk action on multiple users
   * @param request - Bulk action request with user IDs and action type
   * @param performedBy - Admin user ID performing the bulk action
   * @param performedByName - Admin user name performing the bulk action
   * @returns Result with success/failure counts
   */
  static async performBulkAction(
    request: BulkActionRequest,
    performedBy: string,
    performedByName: string
  ): Promise<BulkActionResult> {
    const result: BulkActionResult = {
      action: request.action,
      totalRequested: request.userIds.length,
      successful: 0,
      failed: 0,
      errors: [],
    };

    for (const userId of request.userIds) {
      try {
        switch (request.action) {
          case 'approve':
            await this.approveUser(userId, performedBy);
            break;
          case 'reject':
            await this.rejectUser(userId, performedBy, request.reason);
            break;
          case 'delete':
            await this.deleteUser(userId, performedBy, performedByName, request.reason);
            break;
          case 'change_role':
            if (!request.targetRole) {
              throw new Error('Target role required for role change');
            }
            await this.updateUserRole(userId, request.targetRole, performedBy, performedByName);
            break;
          case 'suspend':
            await this.suspendUser(userId, performedBy, performedByName, request.reason || 'Bulk suspension');
            break;
          case 'unsuspend':
            await this.unsuspendUser(userId, performedBy, performedByName);
            break;
          default:
            throw new Error(`Unsupported bulk action: ${request.action}`);
        }
        result.successful++;
      } catch (error: any) {
        result.failed++;
        result.errors.push({
          userId,
          error: error.message || 'Unknown error',
        });
      }
    }

    // Log the bulk action
    await auditLogService.logAction({
      userId: performedBy,
      action: 'bulk_action',
      details: {
        action: request.action,
        totalRequested: result.totalRequested,
        successful: result.successful,
        failed: result.failed,
        targetRole: request.targetRole,
        reason: request.reason,
      },
    });

    return result;
  }

  // ============================================================================
  // Statistics and Analytics
  // ============================================================================

  /**
   * Get comprehensive user statistics
   * @returns User statistics for admin dashboard
   */
  static async getUserStatistics(): Promise<UserStats> {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));

      const stats: UserStats = {
        totalUsers: 0,
        activeUsers: 0,
        pendingApproval: 0,
        incompleteProfiles: 0,
        usersByRole: {
          viewer: 0,
          contributor: 0,
          editor: 0,
          admin: 0,
        },
        recentRegistrations: 0,
      };

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      const oneMonthAgo = new Date();
      oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);

      usersSnapshot.forEach((doc) => {
        const user = doc.data();
        stats.totalUsers++;

        if (!user.isApproved) {
          stats.pendingApproval++;
        }

        if (!user.profileComplete) {
          stats.incompleteProfiles++;
        }

        if (user.role in stats.usersByRole) {
          stats.usersByRole[user.role as UserRole]++;
        }

        const lastLoginAt = toDate(user.lastLoginAt);
        if (lastLoginAt && lastLoginAt > oneWeekAgo) {
          stats.activeUsers++;
        }

        const createdAt = toDate(user.createdAt);
        if (createdAt && createdAt > oneWeekAgo) {
          stats.recentRegistrations++;
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting user statistics:', error);
      throw new Error('Failed to retrieve user statistics');
    }
  }

  /**
   * Get admin dashboard statistics
   * @returns Comprehensive admin statistics
   */
  static async getAdminStats(): Promise<AdminStats> {
    try {
      const userStats = await this.getUserStatistics();

      // Get content stats (simplified - would query actual collections in production)
      const personsSnapshot = await getDocs(collection(db, 'persons'));
      const photosSnapshot = await getDocs(collection(db, 'photos'));
      const relationshipsSnapshot = await getDocs(collection(db, 'relationships'));

      const oneWeekAgo = new Date();
      oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

      let personsAddedThisWeek = 0;
      let photosUploadedThisWeek = 0;

      personsSnapshot.forEach((doc) => {
        const data = doc.data();
        const createdAt = toDate(data.createdAt);
        if (createdAt && createdAt > oneWeekAgo) {
          personsAddedThisWeek++;
        }
      });

      photosSnapshot.forEach((doc) => {
        const data = doc.data();
        const uploadedAt = toDate(data.uploadedAt);
        if (uploadedAt && uploadedAt > oneWeekAgo) {
          photosUploadedThisWeek++;
        }
      });

      // Get moderation stats
      const flagsSnapshot = await getDocs(
        query(collection(db, 'flags'), where('status', '==', 'pending'))
      );

      const flagsByType: Record<'person' | 'photo' | 'comment' | 'relationship' | 'document' | 'memory' | 'note' | 'research-note', number> = {
        person: 0,
        photo: 0,
        comment: 0,
        relationship: 0,
        document: 0,
        memory: 0,
        note: 0,
        'research-note': 0,
      };

      flagsSnapshot.forEach((doc) => {
        const flag = doc.data();
        if (flag.contentType in flagsByType) {
          flagsByType[flag.contentType as keyof typeof flagsByType]++;
        }
      });

      // Get link requests stats
      const linkRequestsSnapshot = await getDocs(collection(db, 'personLinkRequests'));
      const linkRequestsByStatus = {
        pending: 0,
        approved: 0,
        rejected: 0,
      };

      linkRequestsSnapshot.forEach((doc) => {
        const data = doc.data();
        const status = data.status as 'pending' | 'approved' | 'rejected';
        if (status in linkRequestsByStatus) {
          linkRequestsByStatus[status]++;
        }
      });

      const stats: AdminStats = {
        users: {
          total: userStats.totalUsers,
          pending: userStats.pendingApproval,
          approved: userStats.totalUsers - userStats.pendingApproval,
          suspended: 0, // Would need to query for suspended users
          byRole: userStats.usersByRole,
          newThisWeek: userStats.recentRegistrations,
          newThisMonth: 0, // Would need date calculation
          activeThisWeek: userStats.activeUsers,
        },
        content: {
          totalPersons: personsSnapshot.size,
          totalPhotos: photosSnapshot.size,
          totalRelationships: relationshipsSnapshot.size,
          totalComments: 0, // Would need to query comments
          totalDocuments: 0, // Would need to query documents
          totalMemories: 0, // Would need to query memories
          totalNotes: 0, // Would need to query notes
          totalResearchNotes: 0, // Would need to query research notes
          personsAddedThisWeek,
          photosUploadedThisWeek,
          documentsUploadedThisWeek: 0, // Would need date calculation
          memoriesPublishedThisWeek: 0, // Would need date calculation
        },
        moderation: {
          pendingFlags: flagsSnapshot.size,
          flagsByType,
          flagsResolvedThisWeek: 0, // Would need date calculation
        },
        linkRequests: {
          total: linkRequestsSnapshot.size,
          pending: linkRequestsByStatus.pending,
          approved: linkRequestsByStatus.approved,
          rejected: linkRequestsByStatus.rejected,
        },
        system: {
          lastBackup: undefined,
          storageUsedMB: 0, // Would need Firebase Storage API
          storageQuotaMB: 5120, // Default 5GB
        },
      };

      return stats;
    } catch (error) {
      console.error('Error getting admin stats:', error);
      throw new Error('Failed to retrieve admin statistics');
    }
  }

  /**
   * Get user activity summary
   * @param userId - User ID to get activity for
   * @returns User activity statistics
   */
  private static async getUserStats(userId: string): Promise<{
    loginCount: number;
    personsCreated: number;
    photosUploaded: number;
    commentsCreated: number;
    relationshipsCreated: number;
  }> {
    try {
      // In production, these would be aggregated from activity logs
      // For now, return simplified stats by querying collections

      const personsQuery = query(
        collection(db, 'persons'),
        where('createdBy', '==', userId)
      );
      const personsSnapshot = await getDocs(personsQuery);

      const photosQuery = query(
        collection(db, 'photos'),
        where('uploadedBy', '==', userId)
      );
      const photosSnapshot = await getDocs(photosQuery);

      const relationshipsQuery = query(
        collection(db, 'relationships'),
        where('createdBy', '==', userId)
      );
      const relationshipsSnapshot = await getDocs(relationshipsQuery);

      return {
        loginCount: 0, // Would need activity log
        personsCreated: personsSnapshot.size,
        photosUploaded: photosSnapshot.size,
        commentsCreated: 0, // Would need comments collection
        relationshipsCreated: relationshipsSnapshot.size,
      };
    } catch (error) {
      console.error('Error getting user stats:', error);
      return {
        loginCount: 0,
        personsCreated: 0,
        photosUploaded: 0,
        commentsCreated: 0,
        relationshipsCreated: 0,
      };
    }
  }

  // ============================================================================
  // Helper Methods
  // ============================================================================

  /**
   * Create a suspension record
   * @param suspension - Suspension data
   */
  private static async createSuspensionRecord(
    suspension: Omit<UserSuspension, 'id'>
  ): Promise<void> {
    try {
      const suspensionRef = doc(collection(db, 'suspensions'));
      await setDoc(suspensionRef, {
        ...suspension,
        suspendedAt: Timestamp.fromDate(suspension.suspendedAt),
        createdAt: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error creating suspension record:', error);
    }
  }

  /**
   * Update suspension record when user is unsuspended
   * @param userId - User ID
   * @param unsuspendedBy - Admin who unsuspended
   */
  private static async updateSuspensionRecord(
    userId: string,
    unsuspendedBy: string
  ): Promise<void> {
    try {
      const q = query(
        collection(db, 'suspensions'),
        where('userId', '==', userId),
        where('unsuspendedAt', '==', null),
        limit(1)
      );

      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const suspensionDoc = snapshot.docs[0];
        await updateDoc(doc(db, 'suspensions', suspensionDoc.id), {
          unsuspendedBy,
          unsuspendedAt: serverTimestamp(),
        });
      }
    } catch (error) {
      console.error('Error updating suspension record:', error);
    }
  }

  /**
   * Search users by name or email
   * @param searchTerm - Search term
   * @param limit - Maximum number of results
   * @returns Array of matching users
   */
  static async searchUsers(searchTerm: string, limitCount: number = 10): Promise<UserListItem[]> {
    try {
      // Firestore doesn't support full-text search, so we'll fetch all users
      // and filter in memory (not optimal for large datasets)
      const snapshot = await getDocs(collection(db, 'users'));
      const searchLower = searchTerm.toLowerCase();

      const users: UserListItem[] = [];

      snapshot.forEach((doc) => {
        const data = doc.data();
        const displayName = (data.displayName || '').toLowerCase();
        const email = (data.email || '').toLowerCase();

        if (displayName.includes(searchLower) || email.includes(searchLower)) {
          users.push({
            uid: doc.id,
            email: data.email,
            displayName: data.displayName,
            role: data.role,
            isApproved: data.isApproved,
            profileComplete: data.profileComplete,
            isEmailVerified: data.isEmailVerified,
            lastLoginAt: toDate(data.lastLoginAt),
            createdAt: toDate(data.createdAt) || new Date(),
            personId: data.personId,
          });
        }
      });

      return users.slice(0, limitCount);
    } catch (error) {
      console.error('Error searching users:', error);
      throw new Error('Failed to search users');
    }
  }
}

// Export singleton instance
export const adminService = AdminService;
